import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Indonesian mirror of the police-clearance guide. */
const TITLE = "Surat keterangan catatan kepolisian Thailand untuk dipakai di luar negeri — panduan lengkap";
const DESC =
  "Cara mengurus surat keterangan catatan kepolisian Thailand, dokumen yang perlu disiapkan, urutan pengesahan lanjutan, dan alasan berkas ditolak.";
const PATH = "/blog/police-clearance-certificate-guide";
const URL = `/id${PATH}`;
const PUBLISHED = "2026-08-11";

const FAQ = [
  {
    question: "Instansi mana yang menerbitkan surat ini?",
    answer:
      "Kepolisian Kerajaan Thailand melalui unit rekam jejak kriminal di Bangkok. Pemohon di luar Thailand umumnya mengurus melalui kedutaan atau kuasa hukum di Thailand.",
  },
  {
    question: "Dokumen apa yang perlu disiapkan?",
    answer:
      "Paspor, dokumen identitas Thailand bila ada, formulir permohonan, dua set sidik jari resmi, foto, dan surat kuasa bila diurus oleh perwakilan.",
  },
  {
    question: "Apakah sidik jari harus diambil di Thailand?",
    answer:
      "Tidak selalu. Sidik jari dapat diambil di kantor polisi setempat atau kedutaan Thailand di negara Anda, lalu dikirim bersama berkas permohonan.",
  },
  {
    question: "Berapa lama prosesnya?",
    answer:
      "Waktu proses ditentukan instansi penerbit dan bergantung pada kelengkapan berkas. Konfirmasikan estimasi terbaru sebelum menghitung tenggat pengajuan visa.",
  },
  {
    question: "Apakah surat ini perlu dilegalisasi lagi?",
    answer:
      "Bila dipakai di luar negeri, biasanya perlu pengesahan Departemen Konsuler Kementerian Luar Negeri Thailand, lalu legalisasi kedutaan negara tujuan bila diminta.",
  },
  {
    question: "Berapa lama masa berlakunya?",
    answer:
      "Surat tidak memiliki masa berlaku tunggal; instansi penerima menetapkan batas usia dokumen, sering 3–6 bulan sejak tanggal terbit.",
  },
  {
    question: "Apa penyebab penolakan paling umum?",
    answer:
      "Sidik jari tidak jelas, ejaan nama tidak konsisten dengan paspor, surat kuasa tidak mencakup kewenangan yang diperlukan, dan urutan pengesahan yang tertukar.",
  },
];

export const Route = createFileRoute("/id/blog/police-clearance-certificate-guide")({
  component: IdPccGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "id" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "id") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "surat keterangan catatan kepolisian Thailand, police clearance Thailand, legalisasi dokumen Thailand, SKCK Thailand untuk visa",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "id"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Beranda", url: "/id" },
              { name: "Panduan", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ d: string; note: string }> = [
  { d: "Paspor yang masih berlaku", note: "Salinan halaman data ditambah halaman visa Thailand bila ada" },
  { d: "Dokumen identitas Thailand", note: "KTP atau kartu izin tinggal bila pemohon pernah tinggal di Thailand" },
  { d: "Dua set sidik jari resmi", note: "Diambil di kantor polisi atau kedutaan, pada formulir standar" },
  { d: "Formulir permohonan", note: "Isi nama sesuai ejaan paspor, tanpa singkatan" },
  { d: "Foto terbaru", note: "Sesuai ukuran yang diminta instansi penerbit" },
  { d: "Surat kuasa", note: "Wajib bila permohonan diurus perwakilan; sebutkan kewenangan mengajukan dan menerima" },
];

const ORDER: string[] = [
  "Minta daftar resmi dari instansi penerima di negara tujuan",
  "Ambil sidik jari resmi dan siapkan salinan identitas",
  "Ajukan permohonan ke unit rekam jejak kriminal, langsung atau melalui perwakilan",
  "Terima surat dan periksa ejaan nama, tanggal lahir, serta nomor paspor",
  "Ajukan pengesahan ke Departemen Konsuler Kementerian Luar Negeri Thailand",
  "Terjemahkan bila diminta, lalu lakukan legalisasi kedutaan negara tujuan",
];

const CHECKS: string[] = [
  "Ejaan nama identik pada paspor, formulir, dan terjemahan",
  "Sidik jari jelas dan tidak terpotong pada formulir",
  "Usia dokumen masih dalam batas yang diterima instansi penerima",
  "Terjemahan mencakup stempel, segel, dan catatan kaki",
  "Urutan pengesahan tidak tertukar dengan urutan penerjemahan",
  "Menyiapkan satu set salinan untuk arsip pribadi",
];

function IdPccGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/id" className="text-muted-foreground hover:text-foreground">
          Beranda
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Panduan
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="id" />
      <p className="answer-summary speakable-summary">
        Surat keterangan catatan kepolisian Thailand diterbitkan kepolisian di Bangkok atas dasar sidik jari resmi dan
        identitas pemohon. Untuk dipakai di luar negeri, surat biasanya perlu pengesahan Departemen Konsuler, kemudian
        terjemahan dan legalisasi kedutaan negara tujuan bila diminta.
      </p>

      <h2>1. Dokumen yang perlu disiapkan</h2>
      <ul>
        {DOCS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Urutan pengerjaan</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Titik periksa sebelum mengajukan</h2>
      <ul>
        {CHECKS.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Pertanyaan yang sering diajukan</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Sumber resmi dan layanan terkait</h2>
      <p className="text-sm">
        Referensi:{" "}
        <a href="https://www.royalthaipolice.go.th/" target="_blank" rel="noreferrer">
          Kepolisian Kerajaan Thailand
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Departemen Konsuler, Kementerian Luar Negeri Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/id/pcc" className="text-primary underline">
            Layanan surat keterangan catatan kepolisian
          </Link>
        </li>
        <li>
          <Link to="/id/lgz" className="text-primary underline">
            Pengesahan dan legalisasi dokumen
          </Link>
        </li>
        <li>
          <Link to="/id/notary" className="text-primary underline">
            Notarisasi oleh advokat Notarial Services Attorney
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Konten ditinjau pada {PUBLISHED}. Persyaratan tiap instansi dan negara dapat berbeda, jadi konfirmasikan daftar
        resmi sebelum memulai. Bila Anda tidak ingin mengurus tiap langkah sendiri, tim kami dengan pengalaman lebih dari
        15 tahun dapat memberi konsultasi dan menangani seluruh proses — hubungi kami melalui telepon, LINE, atau email.
      </p>
    </article>
  );
}
